<div class="page-head">
  <div>
    <h1 class="page-title">
      Risk
      <span class="pill pill-ready">Live</span>
    </h1>
    <div class="page-sub">
      Where risk sits in the portfolio, and how drawdowns have unfolded
      historically.
    </div>
  </div>
  <div class="row">
    <button class="btn btn-ghost btn-sm" type="button">Stress test</button>
    <button class="btn btn-primary btn-sm" type="button">Ask Alti</button>
  </div>
</div>

@if (!isLoading) {
  <div class="bento">
    <section class="kpi exp-col-3">
      <span class="label">Expected return</span>
      <span class="value">{{ fmtPct(kpi.expReturn) }}</span>
      <span class="delta">Annualized</span>
    </section>
    <section class="kpi exp-col-3">
      <span class="label">Volatility</span>
      <span class="value">{{ fmtPct(kpi.vol) }}</span>
      <span class="delta">Annualized</span>
    </section>
    <section class="kpi exp-col-3">
      <span class="label">Sharpe</span>
      <span class="value">{{ fmtNum(kpi.sharpe) }}</span>
      <span class="delta">Risk-adjusted</span>
    </section>
    <section class="kpi exp-col-3">
      <span class="label">VaR 95</span>
      <span class="value neg">{{ fmtPct(kpi.varPct) }}</span>
      <span class="delta">1-day historical</span>
    </section>
    @if (treemapNodes.length) {
      <section class="card exp-col-8">
        <div class="card-head">
          <div class="card-title">
            Hot spots <span class="hint">Risk contribution by constituent</span>
          </div>
        </div>
        <app-exp-treemap
          [nodes]="treemapNodes"
          [format]="fmtPct.bind(this)"
        ></app-exp-treemap>
      </section>
    }
    <section class="card exp-col-4">
      <div class="card-head">
        <div class="card-title">Alti · Risk brief</div>
        <span class="alti-chip">Alti</span>
      </div>
      <div class="insights-stack">
        @if (topRisk) {
          <div class="insight">
            <div class="insight-head">
              <span class="alti-chip">Alti</span
              ><span class="insight-tag">Concentration</span>
            </div>
            <p>
              <b>{{ topRisk.ticker }}</b> carries
              <b>{{ fmtPct(topRisk.contribution, 1) }}</b> of your risk at just
              <b>{{ fmtPct(topRisk.weight, 1) }}</b> weight.
            </p>
            <div class="insight-actions">
              <button class="btn btn-ghost btn-sm" type="button">
                Cap at 15%
              </button>
              <button class="btn btn-text btn-sm" type="button">Explain</button>
            </div>
          </div>
        }
        <div class="insight">
          <div class="insight-head">
            <span class="alti-chip">Alti</span
            ><span class="insight-tag">Next step</span>
          </div>
          <p>
            Rolling vol stays inside the band. Consider running a rate-shock
            stress test to size tail risk.
          </p>
        </div>
      </div>
    </section>
    @if (allocRiskRows.length) {
      <section class="card exp-col-6">
        <div class="card-head">
          <div class="card-title">Allocation vs risk</div>
        </div>
        <app-alloc-risk-bars
          [rows]="allocRiskRows"
          [format]="fmtPct.bind(this)"
        ></app-alloc-risk-bars>
      </section>
    }
    @if (marginalRows.length) {
      <section class="card exp-col-6">
        <div class="card-head">
          <div class="card-title">
            Marginal risk <span class="hint">Weight × contribution</span>
          </div>
        </div>
        <app-hbar-list
          [rows]="marginalRows"
          [format]="fmtPct.bind(this)"
          colorMode="neg"
        ></app-hbar-list>
      </section>
    }
    @if (corrMatrix.labels.length) {
      <section class="card exp-col-7">
        <div class="card-head">
          <div class="card-title">
            Correlation
            <span class="hint"
              >Top {{ corrMatrix.labels.length }} constituents</span
            >
          </div>
        </div>
        <app-exp-heatmap
          [labels]="corrMatrix.labels"
          [matrix]="corrMatrix.matrix"
          [decimals]="2"
        ></app-exp-heatmap>
      </section>
    }
    @if (drawdownData.length) {
      <section class="card exp-col-5">
        <div class="card-head">
          <div class="card-title">Drawdown</div>
        </div>
        <app-exp-time-area-chart
          [data]="drawdownData"
          seriesName="Drawdown"
          color="#FF5C7A"
          [fillStops]="['rgba(255,92,122,0.45)', 'rgba(255,92,122,0.02)']"
          [valueDecimals]="2"
          valueSuffix="%"
          yAxisFormat="{text}%"
          [anchorAtZero]="true"
          [height]="220"
          [showRangeSelector]="false"
        ></app-exp-time-area-chart>
      </section>
    }
    @if (rollingVolData.length) {
      <section class="card exp-col-12">
        <div class="card-head">
          <div class="card-title">Rolling volatility</div>
        </div>
        <app-exp-time-area-chart
          [data]="rollingVolData"
          seriesName="Rolling vol"
          color="#FFB547"
          [fillStops]="['rgba(255,181,71,0.40)', 'rgba(255,181,71,0.02)']"
          [valueDecimals]="2"
          valueSuffix="%"
          yAxisFormat="{text}%"
          [height]="240"
          [showRangeSelector]="true"
        ></app-exp-time-area-chart>
      </section>
    }
  </div>
}

@if (isLoading) {
  <div class="bento">
    <div class="kpi exp-col-3">
      <div class="shimmer" style="height: 70px"></div>
    </div>
    <div class="kpi exp-col-3">
      <div class="shimmer" style="height: 70px"></div>
    </div>
    <div class="kpi exp-col-3">
      <div class="shimmer" style="height: 70px"></div>
    </div>
    <div class="kpi exp-col-3">
      <div class="shimmer" style="height: 70px"></div>
    </div>
    <div class="card exp-col-12">
      <div class="shimmer" style="height: 320px"></div>
    </div>
  </div>
}
